import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { selectRoles } from "../../features/roleSlice";
import SpecSheetFormAdmin, {
  SPEC_SHEET_ADMIN_ROLES,
} from "./SpecSheetFormAdmin";
import SpecSheetQueue, { SBCF_QUEUE_ROLES } from "./SpecSheetQueue";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState } from "../../components/UI/States";
import { SbcfEmbedContext, Segmented } from "../../components/SBCF/ui";

// SBCF staff side: the spec sheet work queue and the form editor live here,
// split off from the sales-facing /sbcf dashboard.
const SBCFAdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const role = useSelector(selectRoles);
  const canQueue = SBCF_QUEUE_ROLES.some((r) => role?.roles?.includes(r));
  const canFormAdmin = SPEC_SHEET_ADMIN_ROLES.some((r) =>
    role?.roles?.includes(r)
  );

  const navItems = [
    ...(canQueue ? ["Spec Sheet Queue"] : []),
    ...(canFormAdmin ? ["Form Admin"] : []),
  ];

  const initialView = searchParams.get("view") || navItems[0] || "";
  const [selectedNav, setSelectedNav] = useState(initialView);

  useEffect(() => {
    if (selectedNav) setSearchParams({ view: selectedNav });
  }, [selectedNav, setSearchParams]);

  const header = (children) => (
    <PageHeader
      icon={AdminPanelSettingsIcon}
      tone="orange"
      eyebrow="SBCF"
      title="SBCF Admin"
      description="Spec sheet queue, team workload, and form administration."
    >
      {children}
    </PageHeader>
  );

  if (navItems.length === 0) {
    return (
      <PageShell size="md">
        {header()}
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={LockOutlinedIcon}
            title="No SBCF access yet"
            description="You need an SBCF role (SBCF Assigner, SBCF Team, or SBCF Admin) to access this area."
          />
        </div>
      </PageShell>
    );
  }

  const activeView = navItems.includes(selectedNav) ? selectedNav : navItems[0];

  const renderView = () => {
    switch (activeView) {
      case "Spec Sheet Queue":
        return <SpecSheetQueue mode="manage" />;
      case "Form Admin":
        return <SpecSheetFormAdmin />;
      default:
        return null;
    }
  };

  return (
    <PageShell size="2xl">
      {header(
        <Segmented
          className="mt-5"
          ariaLabel="SBCF admin view"
          value={activeView}
          onChange={setSelectedNav}
          options={navItems.map((item) => ({ value: item, label: item }))}
        />
      )}

      <SbcfEmbedContext.Provider value={true}>
        {renderView()}
      </SbcfEmbedContext.Provider>
    </PageShell>
  );
};

export default SBCFAdminDashboard;
